import { useMemo } from 'react';
import { formatCurrency, formatNumber } from '../../lib/utils';
import { CheckCircle2, AlertTriangle, Info } from 'lucide-react';

/* ═══════════════════════════════════════════
   Step-specific totals calculator
   ═══════════════════════════════════════════ */

/* SWA-223 · réplica literal de engine/revenue.py:61 resolve_collection_mode.
   El frontend no puede importar el original, así que se copia su criterio sin
   variarlo: si la key está presente, manda la key sin mirar nada más; si está
   ausente o vacía (escenario heredado, anterior al selector), la presencia de
   segmentos legibles ES el registro de que el usuario eligió segmentada.
   Cualquier cambio en el original debe reflejarse aquí. */
function resolveCollectionMode(a) {
  const raw = a.collection_capture_mode;
  const mode = raw !== undefined && raw !== null ? String(raw).trim() : '';
  if (mode) return mode;
  return parseCollectionSegments(a) ? 'segmentada' : 'perfil_unico';
}

/* Réplica de engine/revenue.py:16 parse_collection_segments. */
function parseCollectionSegments(a) {
  const raw = a.collection_segments_json;
  if (typeof raw !== 'string' || !raw.trim().startsWith('[')) return null;
  try {
    const segs = JSON.parse(raw);
    return Array.isArray(segs) && segs.length > 0 ? segs : null;
  } catch {
    return null;
  }
}

function computeStepTotals(stepId, assumptions, structured) {
  const a = assumptions || {};
  const s = structured || {};
  const typos = s.typologies || [];
  const landSchedule = s.land_schedule || [];
  const budgetItems = s.budget_items || [];
  const indirectCosts = s.indirect_costs || [];
  const collection = s.collection_structure?.[0] || {};

  const totalUnits = typos.reduce((sum, t) => sum + (t.quantity || 0), 0) || (a.total_units || 0);
  const avgPrice = a.avg_price_per_unit || 0;
  const hardPerUnit = a.hard_cost_per_unit || 0;

  // Hard cost: respect capture mode hierarchy
  const budgetMode = a.budget_capture_mode || 'general';
  let totalHard;
  let hardSource;
  if (budgetMode === 'detailed' && budgetItems.length > 0) {
    totalHard = budgetItems.filter(b => b.is_hard_cost !== false).reduce((s, b) => s + (b.total_amount || 0), 0);
    hardSource = 'Partidas detalladas';
  } else {
    const override = parseFloat(a.hard_cost_total_override) || 0;
    if (override > 0) {
      totalHard = override;
      hardSource = 'Override manual';
    } else {
      totalHard = hardPerUnit * totalUnits;
      hardSource = 'Cálculo por tipología';
    }
  }

  // Urbanization: amount is ADDITIVE to hard costs
  const urbAmount = parseFloat(a.hard_cost_urbanizacion_amount) || 0;
  if (urbAmount > 0) {
    totalHard += urbAmount;
  }

  const ingressBruto = totalUnits * avgPrice;

  switch (stepId) {
    case 'general': {
      const terreno = a.total_land_area_m2 || 0;
      const construible = a.total_buildable_area_m2 || 0;
      const cus = a.cus || 0;
      const ratio = terreno > 0 ? construible / terreno : 0;
      const valid = cus <= 0 || ratio <= cus * 1.01;
      return {
        metrics: [
          { label: 'Terreno', value: `${formatNumber(terreno, 0)} m²` },
          { label: 'Construible', value: `${formatNumber(construible, 0)} m²` },
          { label: 'Ratio', value: `${formatNumber(ratio, 2)}`, suffix: cus > 0 ? ` / CUS ${cus}` : '' },
        ],
        status: valid ? 'ok' : 'warning',
        message: valid ? '' : 'Construible excede CUS',
      };
    }
    case 'typologies': {
      const ingreso = typos.reduce((s, t) => s + (t.quantity || 0) * (t.list_price || 0), 0);
      const costoDir = typos.reduce((s, t) => s + (t.quantity || 0) * (t.direct_cost_per_unit || 0), 0);
      const totalArea = typos.reduce((s, t) => s + (t.area_m2 || 0) * (t.quantity || 0), 0);
      const ppm2 = totalArea > 0 ? ingreso / totalArea : 0;
      return {
        metrics: [
          { label: 'Unidades', value: formatNumber(totalUnits, 0) },
          { label: 'Ingreso bruto', value: formatCurrency(ingreso) },
          { label: 'Costo directo', value: formatCurrency(costoDir) },
          { label: '$/m² prom.', value: formatCurrency(ppm2, 0) },
        ],
        status: totalUnits > 0 ? 'ok' : 'warning',
        message: totalUnits === 0 ? 'Sin tipologías' : '',
      };
    }
    case 'land': {
      const landCost = a.land_cost || 0;
      const scheduleTotal = landSchedule.reduce((s, p) => s + (p.is_percentage ? (p.amount || 0) * landCost : (p.amount || 0)), 0);
      return {
        metrics: [
          { label: 'Terreno', value: formatCurrency(landCost) },
          { label: 'Calendario', value: formatCurrency(scheduleTotal) },
          { label: 'Total terreno', value: formatCurrency(landCost + scheduleTotal), highlight: true },
        ],
        status: 'ok',
      };
    }
    case 'budget': {
      const softPct = a.soft_cost_pct || 0;
      const contingencyPct = a.contingency_pct || 0;
      const devFee = a.development_fee || 0;
      const softTotal = totalHard * softPct;
      const contingency = (totalHard + softTotal) * contingencyPct;
      const total = totalHard + softTotal + contingency + devFee;
      // Check if detailed items exist but mode is general
      const hasDetailedItems = budgetItems.length > 0 && budgetMode !== 'detailed';
      const detailedSum = budgetItems.reduce((s, b) => s + (b.total_amount || 0), 0);
      return {
        metrics: [
          { label: 'Duros', value: formatCurrency(totalHard) },
          { label: 'Blandos', value: formatCurrency(softTotal) },
          { label: 'Contingencia', value: formatCurrency(contingency) },
          { label: 'Total presupuesto', value: formatCurrency(total), highlight: true },
        ],
        status: 'ok',
        message: `Fuente: ${hardSource}`,
      };
    }
    case 'indirect': {
      const months = (a.presale_months || 0) + (a.construction_months || 24) + (a.post_construction_months || 6);
      const admin = (a.admin_monthly || 0) * months;
      const supervision = (a.supervision_monthly || 0) * (a.construction_months || 24);
      const marketing = (a.marketing_budget_pct || 0) * ingressBruto;
      const commission = (a.sales_commission_pct || 0) * ingressBruto;
      const insurance = (a.insurance_pct || 0) * totalHard;
      // L-60 · la key es `post_sale_per_unit` (con guion bajo): `postsale_per_unit`
      // no existe en el catálogo, así que este término valía 0 siempre y el total
      // de indirectos omitía la postventa en silencio.
      const postsale = (a.post_sale_per_unit || 0) * totalUnits;
      const total = admin + supervision + marketing + commission + insurance + postsale;
      const pctOfRevenue = ingressBruto > 0 ? total / ingressBruto * 100 : 0;
      return {
        metrics: [
          { label: 'Admin', value: formatCurrency(admin) },
          { label: 'Marketing', value: formatCurrency(marketing) },
          { label: 'Comisión', value: formatCurrency(commission) },
          { label: 'Total indirectos', value: formatCurrency(total), highlight: true },
        ],
        status: pctOfRevenue > 12 ? 'warning' : 'ok',
        message: pctOfRevenue > 12 ? `${pctOfRevenue.toFixed(1)}% del ingreso (rango típico: 6-10%)` : `${pctOfRevenue.toFixed(1)}% del ingreso`,
      };
    }
    case 'schedule': {
      const pre = a.presale_months || 0;
      const constr = a.construction_months || 24;
      const post = a.post_construction_months || 6;
      const total = pre + constr + post;
      return {
        metrics: [
          { label: 'Preventa', value: `${pre} meses` },
          { label: 'Obra', value: `${constr} meses` },
          { label: 'Post-obra', value: `${post} meses` },
          { label: 'Plazo total', value: `${total} meses`, highlight: true },
        ],
        status: 'ok',
      };
    }
    case 'sales': {
      const abs = a.absorption_rate_units_per_month || 0;
      const constrMonths = a.construction_months || 12;
      const constrFactor = a.construction_sales_factor || 0.6;
      const mesesVenta = abs > 0 ? Math.ceil(totalUnits / abs) : 0;
      // Effective absorption during construction
      const absConstr = abs * constrFactor;
      const soldDuringConstr = Math.min(absConstr * constrMonths, totalUnits);
      const inventoryAtEnd = Math.max(0, Math.round(totalUnits - soldDuringConstr));
      const showInventoryWarning = inventoryAtEnd > 2 && constrFactor < 1;
      return {
        metrics: [
          { label: 'Unidades', value: formatNumber(totalUnits, 0) },
          { label: 'Absorción', value: `${formatNumber(abs, 1)} uds/mes` },
          { label: 'Abs. en obra', value: `${formatNumber(absConstr, 1)} uds/mes (×${constrFactor})` },
          { label: 'Ingreso bruto', value: formatCurrency(ingressBruto), highlight: true },
        ],
        status: showInventoryWarning ? 'warning' : 'ok',
        message: showInventoryWarning ? `Factor de venta en obra = ${(constrFactor*100).toFixed(0)}% → vendes ${formatNumber(absConstr,1)} uds/mes durante construcción → ~${inventoryAtEnd} viviendas en inventario al terminar obra.` : '',
      };
    }
    case 'collection': {
      // SWA-223 · misma precedencia que el motor (orchestrator.py:302-322): el
      // sync desde collection_structure solo corre en 'segmentada'. En
      // 'perfil_unico' el footer muestra los escalares que el usuario teclea.
      // `??` y no `||`: en el motor el sync es `if cs_val is not None`, así que
      // un 0% capturado es un valor válido y no debe caer al siguiente término.
      const segmentada = resolveCollectionMode(a) === 'segmentada';
      const pick = (key) => (segmentada ? (collection[key] ?? a[key] ?? 0) : (a[key] ?? 0));
      const eng = parseFloat(pick('enganche_pct')) || 0;
      const parc = parseFloat(pick('parcialidades_pct')) || 0;
      const esc = parseFloat(pick('pago_escrituracion_pct')) || 0;
      const sum = eng + parc + esc;
      const valid = Math.abs(sum - 1) < 0.005;
      return {
        metrics: [
          { label: 'Enganche', value: `${(eng * 100).toFixed(0)}%` },
          { label: 'Parcialidades', value: `${(parc * 100).toFixed(0)}%` },
          { label: 'Escrituración', value: `${(esc * 100).toFixed(0)}%` },
          { label: 'Total', value: `${(sum * 100).toFixed(0)}%`, highlight: true },
        ],
        status: valid ? 'ok' : 'error',
        message: valid ? '' : `Debe sumar 100% (actual: ${(sum * 100).toFixed(0)}%)`,
      };
    }
    case 'financing': {
      // SWA-294 · sin línea de crédito el motor corre 100 % capital y tasa 0
      // (orchestrator.py, override de línea en cero): el pie dice lo mismo.
      const sinLinea = !(Number(a.credit_line_amount) > 0);
      const rate = sinLinea ? 0 : (a.loan_interest_rate_annual || 0);
      const equityPct = sinLinea ? 1 : (a.equity_pct || 0);
      if (sinLinea) {
        return {
          metrics: [
            { label: 'Equity', value: '100%' },
            { label: 'Deuda', value: '0%' },
            { label: 'Tasa', value: 'No aplica' },
          ],
          status: 'ok',
          message: 'Sin línea de crédito: 100 % capital propio',
        };
      }
      return {
        metrics: [
          { label: 'Equity', value: `${(equityPct * 100).toFixed(0)}%` },
          { label: 'Deuda', value: `${((1 - equityPct) * 100).toFixed(0)}%` },
          { label: 'Tasa', value: `${(rate * 100).toFixed(1)}%` },
        ],
        status: equityPct < 0.15 ? 'warning' : 'ok',
        message: equityPct < 0.15 ? 'Equity bajo (<15%)' : '',
      };
    }
    case 'assumptions': {
      // L-60 · la key es `discount_rate`: `discount_rate_annual` no existe, así
      // que el footer mostraba 0.0% mientras el motor descontaba el VAN con el
      // valor real (metrics.py:23 lee `discount_rate`).
      const disc = a.discount_rate || 0;
      const eq = a.equity_required_return || 0;
      const inflConstr = a.construction_inflation_pct || 0;
      const inflPrice = a.price_escalation_pct || 0;
      const diff = inflConstr - inflPrice;
      return {
        metrics: [
          { label: 'Tasa proyecto', value: `${(disc * 100).toFixed(1)}%` },
          { label: 'Rend. capital', value: eq > 0 ? `${(eq * 100).toFixed(1)}%` : '= proyecto' },
          { label: 'Infl. construcción', value: `${(inflConstr * 100).toFixed(1)}%` },
          { label: 'Infl. precios', value: `${(inflPrice * 100).toFixed(1)}%` },
        ],
        status: diff > 0.03 ? 'warning' : 'ok',
        message: diff > 0.03 ? `Diferencial ${(diff * 100).toFixed(1)}pp (>3pp)` : '',
      };
    }
    default:
      return { metrics: [], status: 'ok' };
  }
}

/* ═══════════════════════════════════════════
   Step completeness checker
   ═══════════════════════════════════════════ */

function isStepComplete(stepId, assumptions, structured) {
  const a = assumptions || {};
  const s = structured || {};
  switch (stepId) {
    case 'general': return !!(a.project_type && a.housing_format && a.market_segment && a.total_land_area_m2 > 0);
    case 'typologies': return (s.typologies || []).length > 0;
    case 'land': return a.land_cost !== undefined;  // $0 is valid (en especie)
    case 'budget': return (a.hard_cost_per_unit || 0) > 0 || (a.hard_cost_total_override || 0) > 0;
    case 'indirect': return true; // Optional
    case 'schedule': return (a.construction_months || 0) > 0;
    case 'sales': return (a.avg_price_per_unit || 0) > 0 && (a.absorption_rate_units_per_month || 0) > 0;
    case 'collection': return true; // Has defaults
    case 'financing': return true; // Has defaults
    case 'assumptions': return true; // Has defaults
    case 'review': return true;
    default: return false;
  }
}

/* ═══════════════════════════════════════════
   WizardFooter Component
   ═══════════════════════════════════════════ */

export function WizardFooter({ stepId, assumptions, structured }) {
  const totals = useMemo(
    () => computeStepTotals(stepId, assumptions, structured),
    [stepId, assumptions, structured]
  );

  if (!totals.metrics.length) return null;

  const statusColor = totals.status === 'ok' ? 'var(--success)' : totals.status === 'warning' ? 'var(--warning-color)' : 'var(--danger)';
  const StatusIcon = totals.status === 'ok' ? CheckCircle2 : totals.status === 'warning' ? AlertTriangle : AlertTriangle;

  return (
    <div className="flex items-center gap-6 px-6" data-testid="wizard-footer-totals">
      {totals.metrics.map((m, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider" style={{ color: '#94A3B8' }}>{m.label}</span>
          <span className={`text-sm tabular-nums ${m.highlight ? 'font-semibold' : 'font-medium'}`} style={{ color: m.highlight ? 'var(--text-primary)' : '#64748B' }}>
            {m.value}{m.suffix || ''}
          </span>
        </div>
      ))}
      {totals.message && (
        <div className="flex items-center gap-1 ml-auto">
          <StatusIcon className="w-3.5 h-3.5" style={{ color: statusColor }} />
          <span className="text-[10px]" style={{ color: statusColor }}>{totals.message}</span>
        </div>
      )}
      {!totals.message && totals.status === 'ok' && (
        <CheckCircle2 className="w-3.5 h-3.5 ml-auto" style={{ color: 'var(--success)' }} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   Progress Bar Component
   ═══════════════════════════════════════════ */

export function WizardProgressBar({ steps, currentStep, assumptions, structured, onStepClick }) {
  return (
    <div className="flex items-center gap-1" data-testid="wizard-progress-bar">
      {steps.map((s, i) => {
        const isCurrent = i === currentStep;
        const complete = i < currentStep || (i !== currentStep && isStepComplete(s.id, assumptions, structured));
        const StepIcon = s.icon;

        return (
          <button
            key={s.id}
            onClick={() => onStepClick(i)}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] transition-all duration-150"
            style={{
              background: isCurrent ? 'var(--financial-primary)' : complete ? 'rgba(12,191,115,0.08)' : 'transparent',
              color: isCurrent ? 'white' : complete ? 'var(--success)' : '#94A3B8',
              border: isCurrent ? 'none' : complete ? '1px solid rgba(12,191,115,0.2)' : '1px solid #E2E8F0',
            }}
            data-testid={`progress-step-${i}`}
          >
            {complete && !isCurrent ? (
              <CheckCircle2 className="w-3 h-3" />
            ) : (
              <span className="w-3 h-3 flex items-center justify-center text-[8px] font-bold">{i + 1}</span>
            )}
            <span className="hidden xl:inline truncate max-w-[100px]">{s.shortLabel}</span>
          </button>
        );
      })}
    </div>
  );
}

export { isStepComplete };
